(() => {
  const getForm = (host) => {
    if (host.dataset.formId) return document.getElementById(host.dataset.formId);
    return document.querySelector('.product-information .product-details product-form-component form');
  };
  const getData = (host) => {
    try { return JSON.parse(host.querySelector('[data-main-variants]')?.textContent || '[]'); }
    catch { return []; }
  };
  const money = (host, cents) => new Intl.NumberFormat(host.dataset.locale || 'en', {
    style: 'currency', currency: host.dataset.currency || 'USD', currencyDisplay: 'code'
  }).format(cents / 100);
  const mainState = (host) => {
    const form = getForm(host);
    if (!form) return null;
    const component = form.closest('product-form-component');
    if (component?.dataset.productId && component.dataset.productId !== host.dataset.mainProduct) return null;
    const id = form.querySelector('[name="id"]')?.value;
    const variant = getData(host).find(item => String(item.id) === String(id));
    if (!variant?.available) return null;
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || variant.min || 1);
    if (!Number.isSafeInteger(quantity) || quantity < (variant.min || 1)) return null;
    if (variant.max && quantity > variant.max) return null;
    if ((quantity - (variant.min || 1)) % (variant.increment || 1)) return null;
    return { form, variant, quantity };
  };

  class OpaelgPairing extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('change', () => this.update(), options);
      this.querySelector('[data-pair-add]').addEventListener('click', () => this.add(), options);
      this.scope = this.closest('.shopify-section') || document;
      this.scope.addEventListener('change', () => queueMicrotask(() => this.update()), options);
      this.scope.addEventListener('input', () => this.update(), options);
      this.scope.addEventListener('quantity-selector:update', () => queueMicrotask(() => this.update()), options);
      this.scope.addEventListener('variant:selected', () => {
        this.waiting = true;
        this.update();
      }, options);
      this.scope.addEventListener('variant:update', () => {
        this.waiting = false;
        queueMicrotask(() => this.update());
      }, options);
      if (this.dataset.mode === 'product' && !getForm(this)) return;
      this.hidden = false;
      this.update();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
    }

    update() {
      const checkbox = this.querySelector('[data-pair-check]');
      const select = this.querySelector('[data-pair-variant]');
      const option = select.selectedOptions[0];
      const chosen = checkbox.checked && !!option?.value && !option.disabled;
      const state = this.dataset.mode === 'product' ? mainState(this) : null;
      const nativeBusy = state?.form.querySelector('button[name="add"]')?.disabled;
      const ready = chosen && !this.waiting && !nativeBusy && (this.dataset.mode === 'cart' || !!state);
      checkbox.disabled = !!this.busy;
      select.disabled = !checkbox.checked || !!this.busy;
      this.querySelector('[data-pair-add]').disabled = !ready || !!this.busy || !!this.uncertain;
      this.querySelector('[data-pair-total]').hidden = !ready;
      const amount = Number(option?.dataset.price || 0) + (state ? state.variant.price * state.quantity : 0);
      if (ready) this.querySelector('[data-pair-amount]').textContent = money(this, amount);
      if (option?.value) {
        this.querySelector('[data-pair-price]').textContent = option.dataset.money;
        if (option.dataset.image) this.querySelector('[data-pair-image]').src = option.dataset.image;
        this.querySelector('[data-pair-product-link]').href = this.dataset.productUrl + '?variant=' + encodeURIComponent(option.value);
      }
      this.classList.toggle('op-pair-selected', checkbox.checked);
    }

    navigate(url) { window.location.assign(url); }

    async add() {
      const button = this.querySelector('[data-pair-add]');
      if (button.disabled || this.busy) return;
      const select = this.querySelector('[data-pair-variant]');
      const option = select.selectedOptions[0];
      if (!this.querySelector('[data-pair-check]').checked || !option?.value || option.disabled) return;
      const items = [];
      let lockedForm;
      let previousInert;
      if (this.dataset.mode === 'product') {
        const state = mainState(this);
        if (!state || state.form.querySelector('button[name="add"]')?.disabled || !state.form.reportValidity()) { this.update(); return; }
        const formData = new FormData(state.form);
        const item = { id: Number(state.variant.id), quantity: state.quantity };
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
        }
        if (Object.keys(properties).length) item.properties = properties;
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = Number(sellingPlan);
        items.push(item);
        lockedForm = state.form;
        previousInert = lockedForm.inert;
      }
      const pairedId = Number(option.value);
      if (!Number.isSafeInteger(pairedId) || pairedId <= 0) return;
      items.push({ id: pairedId, quantity: 1 });
      this.busy = true;
      if (lockedForm) lockedForm.inert = true;
      this.setAttribute('aria-busy', 'true');
      this.dispatchEvent(new Event('opaelg:pair-state', { bubbles: true }));
      const status = this.querySelector('[data-pair-status]');
      status.textContent = this.dataset.adding;
      this.update();
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        let result;
        try { result = await response.json(); } catch { throw new Error(this.dataset.error); }
        if (!response.ok || result.status >= 400) {
          throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        }
        status.textContent = this.dataset.added;
        this.dispatchEvent(new CustomEvent('opaelg:pair-added', {
          bubbles: true, detail: { items: items.map(item => ({ id: item.id, quantity: item.quantity })) }
        }));
        this.navigate(this.dataset.cartUrl);
      } catch (error) {
        this.uncertain = true;
        status.textContent = (error.message || this.dataset.error) + ' ' + this.dataset.checkBag;
        this.querySelector('[data-pair-recovery]').hidden = false;
      } finally {
        this.busy = false;
        if (lockedForm) lockedForm.inert = !!previousInert;
        this.removeAttribute('aria-busy');
        this.dispatchEvent(new Event('opaelg:pair-state', { bubbles: true }));
        this.update();
      }
    }
  }

  class OpaelgStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.form = getForm(this);
      if (!this.form) return;
      this.scope = this.form.closest('.shopify-section') || document;
      if (this.scope.querySelector('[data-op-sticky-setting="false"]')) return;
      this.scope.addEventListener('variant:selected', () => { this.waiting = true; this.update(); }, options);
      this.scope.addEventListener('variant:update', () => { this.waiting = false; queueMicrotask(() => this.update()); }, options);
      this.scope.addEventListener('change', () => queueMicrotask(() => this.update()), options);
      this.scope.addEventListener('input', () => this.update(), options);
      this.scope.addEventListener('quantity-selector:update', () => queueMicrotask(() => this.update()), options);
      this.scope.addEventListener('opaelg:pair-state', () => this.update(), options);
      this.querySelector('button').addEventListener('click', () => this.submit(), options);
      document.addEventListener('click', event => {
        if (event.target.closest('a,button,input,select,summary')) queueMicrotask(() => this.update());
      }, options);
      document.addEventListener('cart:update', () => { this.busy = false; this.update(); }, options);
      document.addEventListener('cart:error', () => { this.busy = false; this.update(); }, options);
      this.update();
    }

    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      clearTimeout(this.unlock);
      document.body.classList.remove('op-has-sticky');
    }

    update() {
      const state = mainState(this);
      this.hidden = false;
      document.body.classList.toggle('op-has-sticky', !this.hidden);
      const button = this.querySelector('button');
      const nativeButton = getForm(this)?.querySelector('button[name="add"]');
      button.disabled = !state || this.waiting || this.busy || !!nativeButton?.disabled || !!getForm(this)?.inert;
      this.querySelector('[data-sticky-price]').textContent = state ? money(this, state.variant.price * state.quantity) : '';
      this.querySelector('[data-sticky-variant]').textContent = state?.variant.title || this.dataset.choose;
    }

    submit() {
      const button = this.querySelector('button');
      if (button.disabled) return;
      const state = mainState(this);
      const nativeButton = state?.form.querySelector('button[name="add"]');
      if (!state || !nativeButton || nativeButton.disabled || !state.form.reportValidity()) return;
      this.busy = true;
      this.update();
      nativeButton.click();
      this.unlock = setTimeout(() => { this.busy = false; this.update(); }, 10000);
    }
  }

  if (!customElements.get('opaelg-pairing')) customElements.define('opaelg-pairing', OpaelgPairing);
  if (!customElements.get('opaelg-sticky-atc')) customElements.define('opaelg-sticky-atc', OpaelgStickyAtc);
})();
